// Two transport modes for hello/heartbeat/curated-activity + the "is the
// interview over?" signal. Mode is decided once, at boot, from the window
// hierarchy. Chunks (/chunks) are NOT part of this module -- they always go
// through outbox.ts regardless of mode.
//
// iframe mode: the recorder page is embedded in an iframe on the call page.
//   Talks to the parent directly via postMessage (lower latency than a round
//   trip through the backend) AND to the backend via /signal (the backend
//   batches activity server-side and pushes it into the live call, which
//   keeps working even when the parent tab is backgrounded). The parent
//   still needs the heartbeat directly for its own screen-share-fallback
//   detection, so heartbeat goes to BOTH places; activity is /signal-only.
// tab mode: the recorder page is a separate top-level tab (window.open).
//   There's no reachable parent to postMessage at all, so hello/heartbeat/
//   activity all go through /signal, and we poll /poll for the interview's
//   status.
//
// Every /signal body carries a monotonically increasing `seq` (shared across
// hello/heartbeat/activity) so the backend can dedupe a retried send via its
// XADD. The counter is persisted in the `session`-provided Session record
// (mirroring how index.ts persists chunk `lastSeq`) so it survives a
// same-origin navigation instead of restarting at 1 and looking like replays
// of already-seen signals. Sends are serialized through a single promise
// chain so a retry can never race ahead of (or fall behind) a later send.

import { HEARTBEAT_INTERVAL_MS, POLL_INTERVAL_MS, SIGNAL_ACTIVITY_MAX_ATTEMPTS } from './config';
import { fitActivityToCap, type CuratedActivity } from './curated';
import { persistSession } from './session';
import type { Session } from './types';
import { endpoints, postJson } from './wire';

export type ChannelMode = 'iframe' | 'tab';

export interface ChannelDeps {
  recordingId: string;
  token: string;
  /** Parent said close, or poll observed status:'closing' -- drain + finalize. */
  onFinalize: () => void;
  /** Poll observed status: 'aborted' | 'complete' -- stop immediately, no finalize call. */
  onStopAndClear: () => void;
  win?: Window;
  doc?: Document;
  /** Mutable session record used ONLY to persist the monotonic /signal `seq`
   * counter across a same-origin navigation -- mirrors how index.ts persists
   * chunk `lastSeq` via the same Session object. Optional: without it, seq
   * still increments monotonically for this Channel instance's lifetime, it
   * just won't survive a reload (fine for tests that don't care). */
  session?: Session;
  /** Storage passed through to persistSession -- defaults to sessionStorage.
   * Exists purely so tests can inject an isolated stub. */
  storage?: Storage;
}

export function detectMode(win: Window): ChannelMode {
  try {
    return win.parent && win.parent !== win ? 'iframe' : 'tab';
  } catch {
    // Accessing win.parent across a hostile sandbox can throw; if it throws
    // there IS a different parent context, so treat that as iframe mode.
    return 'iframe';
  }
}

function resolveParentOrigin(win: Window): string | null {
  try {
    const ancestorOrigins = (win.location as unknown as { ancestorOrigins?: { length: number; [i: number]: string } })
      .ancestorOrigins;
    if (ancestorOrigins && ancestorOrigins.length > 0) return ancestorOrigins[0] ?? null;
  } catch {
    // ignore
  }
  try {
    if (win.document.referrer) return new URL(win.document.referrer).origin;
  } catch {
    // ignore
  }
  return null;
}

interface IncomingMessage {
  token?: string;
  type?: 'close' | 'ack';
}

export class Channel {
  readonly mode: ChannelMode;
  private readonly win: Window;
  private readonly parentOrigin: string | null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private pollCursor: string | undefined;
  private readonly messageHandler = (evt: MessageEvent) => this.handleMessage(evt);
  /** In-memory fallback seq counter, used only when no `session` dep is
   * supplied (see nextSeq()). */
  private localSeq = 0;
  /** Serializes every /signal POST through a single chain so sends can never
   * race/overlap -- e.g. an activity retry can't get reordered behind a
   * later heartbeat or vice versa. */
  private sendChain: Promise<void> = Promise.resolve();

  constructor(private readonly deps: ChannelDeps) {
    this.win = deps.win ?? window;
    this.mode = detectMode(this.win);
    this.parentOrigin = this.mode === 'iframe' ? resolveParentOrigin(this.win) : null;
  }

  start(): void {
    if (this.mode === 'iframe') {
      this.win.addEventListener('message', this.messageHandler);
    } else {
      this.pollTimer = setInterval(() => void this.poll(), POLL_INTERVAL_MS);
      void this.poll();
    }
    this.sendHello();
    this.heartbeatTimer = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
  }

  /** Stops PRODUCING new signals/heartbeats and stops reacting to incoming
   * messages -- clears the heartbeat/poll timers and removes the message
   * listener. Deliberately does NOT touch `sendChain`: any /signal send
   * already enqueued keeps running to completion, so a subsequent drain()
   * can actually reach empty instead of racing a still-ticking producer. */
  quiesce(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.win.removeEventListener('message', this.messageHandler);
  }

  stop(): void {
    this.quiesce();
  }

  /** Awaits whatever /signal send is CURRENTLY chained onto sendChain (the
   * serialized queue described above `sendChain`'s declaration). Used by the
   * finalize flow so a late click/heartbeat isn't dropped mid-flight when the
   * page is about to tear down. Must be called AFTER quiesce() (or with no
   * further sends expected) for the wait to actually reach "empty" instead of
   * chasing a still-producing timer. */
  drain(): Promise<void> {
    return this.sendChain;
  }

  sendActivity(activity: CuratedActivity): void {
    // Enforce the /signal 8KB cap here -- the single point both transports go
    // through -- so an unbounded navigation URL can never blow the contract.
    const bounded = fitActivityToCap(activity);
    if (!bounded) return; // over cap even after truncation -> dropped (warned in fitActivityToCap)
    // Always /signal, in BOTH modes -- the backend batches activity
    // server-side and pushes it into the live call, so the parent no longer
    // needs it via postMessage (and iframe mode is no better-placed to relay
    // it than the backend is; the reverse used to be true, but the server
    // push works even when the parent tab is backgrounded).
    // occurred_at comes from the ORIGINAL curated emit (the click/nav itself),
    // not send time, so a send delayed behind a retry still reports the true
    // moment it happened.
    void this.enqueueSignal('activity', bounded, bounded.occurred_at);
  }

  sendHello(): void {
    if (this.mode === 'iframe') this.postToParent('hello');
    else void this.enqueueSignal('hello');
  }

  sendHeartbeat(): void {
    // iframe mode: the parent STILL needs the heartbeat directly for its own
    // screen-share-fallback detection, so this goes to BOTH places. Tab mode
    // has no parent to postMessage, so /signal is the only path either way.
    if (this.mode === 'iframe') this.postToParent('heartbeat');
    void this.enqueueSignal('heartbeat');
  }

  private postToParent(type: 'hello' | 'heartbeat', payload?: unknown): void {
    if (!this.parentOrigin) return; // no reachable/known parent origin -- nothing safe to send to
    this.win.parent.postMessage(
      { source: 'ui-recorder', token: this.deps.token, recordingId: this.deps.recordingId, type, payload },
      this.parentOrigin,
    );
  }

  /** Assigns the next monotonic /signal seq and persists it (if a `session`
   * dep was supplied), THEN chains the actual send onto `sendChain` so sends
   * execute strictly in call order, never overlapping. Assigning seq here --
   * synchronously, before the async send runs -- is what makes the seq
   * ordering match call order even though the sends themselves are async. */
  private enqueueSignal(
    type: 'hello' | 'heartbeat' | 'activity',
    data?: unknown,
    occurredAt: number = Date.now(),
  ): Promise<void> {
    const seq = this.nextSeq();
    this.sendChain = this.sendChain.then(
      () => this.deliverSignal(type, data, seq, occurredAt),
      () => this.deliverSignal(type, data, seq, occurredAt), // keep the chain alive even if a previous link somehow rejected
    );
    return this.sendChain;
  }

  private nextSeq(): number {
    const current = this.deps.session ? this.deps.session.signalSeq : this.localSeq;
    const seq = current + 1;
    if (this.deps.session) {
      this.deps.session.signalSeq = seq;
      persistSession(this.deps.session, this.deps.storage);
    } else {
      this.localSeq = seq;
    }
    return seq;
  }

  private async deliverSignal(
    type: 'hello' | 'heartbeat' | 'activity',
    data: unknown,
    seq: number,
    occurredAt: number,
  ): Promise<void> {
    const body = JSON.stringify({ type, data, seq, occurred_at: occurredAt });
    let result = await postJson(endpoints.signal(this.deps.recordingId), this.deps.token, body);
    if (result.ok || type !== 'activity') return;
    // ACTIVITY only: retry a bounded number of times with the SAME seq, so a
    // genuinely-lost send is re-delivered while a response-lost retry is
    // deduped server-side (the backend drops an XADD whose seq it has
    // already stored). Hello/heartbeat stay single-attempt/best-effort -- a
    // dropped heartbeat is superseded by the next one 5s later, and letting
    // it retry here would sit in front of later activity/heartbeat sends in
    // the serialized chain and starve them.
    for (let attempt = 1; attempt < SIGNAL_ACTIVITY_MAX_ATTEMPTS && !result.ok; attempt += 1) {
      result = await postJson(endpoints.signal(this.deps.recordingId), this.deps.token, body);
    }
    if (!result.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `[UserIntuitionRecorder] activity /signal seq=${seq} failed after ${SIGNAL_ACTIVITY_MAX_ATTEMPTS} attempts -- dropped.`,
      );
    }
  }

  private handleMessage(evt: MessageEvent): void {
    if (!this.isTrustedOrigin(evt.origin)) return;
    const msg = evt.data as IncomingMessage | null;
    if (!msg || typeof msg !== 'object') return;
    if (msg.token !== this.deps.token) return;
    if (msg.type === 'close') {
      this.deps.onFinalize();
    }
    // type === 'ack' is informational only; no action needed.
  }

  private isTrustedOrigin(origin: string): boolean {
    if (this.parentOrigin && origin === this.parentOrigin) return true;
    try {
      const ancestorOrigins = (this.win.location as unknown as { ancestorOrigins?: { length: number; [i: number]: string } })
        .ancestorOrigins;
      if (ancestorOrigins) {
        for (let i = 0; i < ancestorOrigins.length; i += 1) {
          if (ancestorOrigins[i] === origin) return true;
        }
      }
    } catch {
      // ignore
    }
    return false;
  }

  private async poll(): Promise<void> {
    const result = await postJson(
      endpoints.poll(this.deps.recordingId),
      this.deps.token,
      JSON.stringify({ cursor: this.pollCursor }),
    );
    if (!result.ok || !result.body || typeof result.body !== 'object') return;
    const body = result.body as { status?: string; next_cursor?: string };
    if (typeof body.next_cursor === 'string') this.pollCursor = body.next_cursor;
    if (body.status === 'closing') {
      this.deps.onFinalize();
    } else if (body.status === 'aborted' || body.status === 'complete') {
      this.deps.onStopAndClear();
    }
  }
}
